
import React, {
    useEffect,
    useState,
    useRef
} from "react";

import {
    Link
} from "react-router-dom";

import {
    FaArrowRight,
    FaUser,
    FaCalendarAlt,
    FaBookOpen,
    FaChevronLeft,
    FaChevronRight
} from "react-icons/fa";

import API_BASE_URL from "../config/config";

import "./BlogList.css";


const BlogList = () => {

    /*
    |--------------------------------------------------------------------------
    | API
    |--------------------------------------------------------------------------
    */

    const API_URL = API_BASE_URL.replace(/\/$/, "");

    const STORAGE_BASE_URL = API_URL.replace(/\/api$/, "");

    const IMAGE_STORAGE_URL = `${STORAGE_BASE_URL}/storage/blog`;


    /*
    |--------------------------------------------------------------------------
    | BLOG SECTION REF
    |--------------------------------------------------------------------------
    */

    const blogSectionRef = useRef(null);


    /*
    |--------------------------------------------------------------------------
    | STATES
    |--------------------------------------------------------------------------
    */

    const [blogs, setBlogs] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);

    const [lastPage, setLastPage] = useState(1);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState("");


    /*
    |--------------------------------------------------------------------------
    | IMAGE URL
    |--------------------------------------------------------------------------
    */

    const getImageUrl = (image) => {

        if (!image) {
            return "";
        }

        if (
            image.startsWith("http://") ||
            image.startsWith("https://")
        ) {
            return image;
        }

        return `${IMAGE_STORAGE_URL}/${image.replace(/^\/+/, "")}`;
    };


    /*
    |--------------------------------------------------------------------------
    | HTML TO PLAIN TEXT
    |--------------------------------------------------------------------------
    */

    const getPlainText = (html) => {

        if (!html) {
            return "";
        }

        const tempDiv = document.createElement("div");

        tempDiv.innerHTML = html;

        return (
            tempDiv.textContent ||
            tempDiv.innerText ||
            ""
        );
    };


    /*
    |--------------------------------------------------------------------------
    | SHORT DESCRIPTION
    |--------------------------------------------------------------------------
    */

    const getShortDescription = (description) => {

        const text = getPlainText(description);

        if (text.length <= 130) {
            return text;
        }

        return `${text.substring(0, 130).trim()}...`;
    };


    /*
    |--------------------------------------------------------------------------
    | DATE FORMAT
    |--------------------------------------------------------------------------
    */

    const formatDate = (date) => {

        if (!date) {
            return "";
        }

        const dateObject = new Date(date);

        if (Number.isNaN(dateObject.getTime())) {
            return date;
        }

        return dateObject.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric"
        });
    };


    /*
    |--------------------------------------------------------------------------
    | FETCH BLOGS
    |--------------------------------------------------------------------------
    */

    const fetchBlogs = async (page = 1) => {

        try {

            setLoading(true);

            setError("");

            const response = await fetch(
                `${API_URL}/user-blogs?per_page=4&page=${page}`
            );

            const data = await response.json();

            console.log("BLOGS:", data);

            if (response.ok && data.success) {

                setBlogs(
                    Array.isArray(data.data)
                        ? data.data
                        : []
                );

                setCurrentPage(
                    Number(data.pagination?.current_page) || page
                );

                setLastPage(
                    Number(data.pagination?.last_page) || 1
                );

            } else {

                setBlogs([]);

                setError(
                    data.message || "Unable to load blogs."
                );

            }

        } catch (error) {

            console.error("BLOG FETCH ERROR:", error);

            setBlogs([]);

            setError("Unable to load blogs.");

        } finally {

            setLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | INITIAL LOAD
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchBlogs(1);

        // Initial API request only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    /*
    |--------------------------------------------------------------------------
    | PAGINATION CLICK
    |--------------------------------------------------------------------------
    */

    const handlePageChange = (page) => {

        if (
            page < 1 ||
            page > lastPage ||
            page === currentPage
        ) {
            return;
        }

        setCurrentPage(page);

        fetchBlogs(page);

        /*
        |--------------------------------------------------------------------------
        | SCROLL TO BLOG SECTION
        |--------------------------------------------------------------------------
        */

        if (blogSectionRef.current) {

            const sectionTop =
                blogSectionRef.current.getBoundingClientRect().top +
                window.pageYOffset;

            window.scrollTo({
                top: sectionTop - 80,
                behavior: "smooth"
            });

        }

    };


    /*
    |--------------------------------------------------------------------------
    | RESPONSIVE VISIBLE PAGE NUMBERS
    |--------------------------------------------------------------------------
    | Show limited page numbers instead of rendering every page.
    | Example: 1 ... 8 9 10 ... 50
    |--------------------------------------------------------------------------
    */

    const getVisiblePages = () => {

        const pages = [];

        const maxVisiblePages = 5;

        if (lastPage <= maxVisiblePages + 2) {

            for (let page = 1; page <= lastPage; page++) {
                pages.push(page);
            }

            return pages;

        }

        // Always show the first page.
        pages.push(1);

        let startPage = Math.max(2, currentPage - 1);

        let endPage = Math.min(lastPage - 1, currentPage + 1);

        // Keep nearby pages visible at the beginning.
        if (currentPage <= 3) {

            startPage = 2;

            endPage = Math.min(lastPage - 1, 4);

        }

        // Keep nearby pages visible at the end.
        if (currentPage >= lastPage - 2) {

            startPage = Math.max(2, lastPage - 3);

            endPage = lastPage - 1;

        }

        // Left ellipsis.
        if (startPage > 2) {
            pages.push("...");
        }

        // Pages around the current page.
        for (
            let page = startPage;
            page <= endPage;
            page++
        ) {
            pages.push(page);
        }

        // Right ellipsis.
        if (endPage < lastPage - 1) {
            pages.push("...");
        }

        // Always show the last page.
        pages.push(lastPage);

        return pages;

    };


    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (
            <section className="blog-section">

                <div className="container">

                    <div className="blog-loading">

                        <div
                            className="spinner-border"
                            role="status"
                            aria-label="Loading blogs"
                        />

                        <p>Loading blogs...</p>

                    </div>

                </div>

            </section>
        );

    }


    /*
    |--------------------------------------------------------------------------
    | ERROR
    |--------------------------------------------------------------------------
    */

    if (error) {

        return (
            <section className="blog-section">

                <div className="container">

                    <div className="alert alert-danger">
                        {error}
                    </div>

                </div>

            </section>
        );

    }


    /*
    |--------------------------------------------------------------------------
    | NO BLOGS
    |--------------------------------------------------------------------------
    */

    if (blogs.length === 0) {

        return (
            <section className="blog-section">

                <div className="container">

                    <div className="blog-empty">

                        <FaBookOpen />

                        <h4>No Blogs Found</h4>

                        <p>
                            Blog posts are currently unavailable.
                        </p>

                    </div>

                </div>

            </section>
        );

    }


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (

        <section
            ref={blogSectionRef}
            className="blog-section"
        >

            <div className="container">

                {/* SECTION HEADING */}

                <div className="blog-section-heading">

                    <span>Latest Articles</span>

                    <h2>
                        Explore Our{" "}
                        <strong>Blogs</strong>
                    </h2>

                    <div className="blog-heading-line" />

                </div>


                {/* BLOG GRID */}

                <div className="row g-4">

                    {blogs.map((blog) => (

                        <div
                            className="col-lg-6"
                            key={blog.blog_id}
                        >

                            <article className="blog-card">

                                {/* IMAGE */}

                                <div className="blog-card-image">

                                    {blog.image ? (

                                        <img
                                            src={getImageUrl(blog.image)}
                                            alt={blog.title || "Blog image"}
                                        />

                                    ) : (

                                        <div className="blog-no-image">

                                            <FaBookOpen />

                                        </div>

                                    )}

                                </div>


                                {/* CONTENT */}

                                <div className="blog-card-content">

                                    {/* META */}

                                    <div className="blog-meta">

                                        <span>

                                            <FaUser />

                                            {blog.author}

                                        </span>


                                        <span>

                                            <FaCalendarAlt />

                                            {formatDate(blog.date_added)}

                                        </span>

                                    </div>


                                    {/* TITLE */}

                                    <h3>
                                        {blog.title}
                                    </h3>


                                    {/* DESCRIPTION */}

                                    <p>
                                        {getShortDescription(blog.description)}
                                    </p>


                                    {/* READ MORE */}

                                    <Link
                                        to={`/blog-detail/${blog.blog_id}`}
                                        className="blog-read-more"
                                    >

                                        Read More

                                        <FaArrowRight />

                                    </Link>

                                </div>

                            </article>

                        </div>

                    ))}

                </div>


                {/* RESPONSIVE PAGINATION */}

                {lastPage > 1 && (

                    <nav
                        className="blog-pagination"
                        aria-label="Blog pagination"
                    >

                        {/* PREVIOUS */}

                        <button
                            type="button"
                            className="pagination-arrow"
                            disabled={currentPage === 1}
                            onClick={() =>
                                handlePageChange(currentPage - 1)
                            }
                            aria-label="Previous page"
                        >

                            <FaChevronLeft />

                        </button>


                        {/* PAGE NUMBERS */}

                        {getVisiblePages().map((page, index) => (

                            page === "..." ? (

                                <span
                                    key={`ellipsis-${index}`}
                                    className="pagination-ellipsis"
                                    aria-hidden="true"
                                >
                                    ...
                                </span>

                            ) : (

                                <button
                                    key={page}
                                    type="button"
                                    className={`pagination-number ${
                                        currentPage === page
                                            ? "active"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        handlePageChange(page)
                                    }
                                    aria-label={`Go to page ${page}`}
                                    aria-current={
                                        currentPage === page
                                            ? "page"
                                            : undefined
                                    }
                                >

                                    {page}

                                </button>

                            )

                        ))}


                        {/* NEXT */}

                        <button
                            type="button"
                            className="pagination-arrow"
                            disabled={currentPage === lastPage}
                            onClick={() =>
                                handlePageChange(currentPage + 1)
                            }
                            aria-label="Next page"
                        >

                            <FaChevronRight />

                        </button>

                    </nav>

                )}

            </div>

        </section>

    );

};

export default BlogList;
